import { forwardRef, JSX, useImperativeHandle, useRef } from 'react';
import ValidationInfo from '../../../validation/validation-info';
import { IdHelper } from '../../../helpers/id.helper';
import RequiredAsterisk from '../RequiredAsterisk';
import './style.scss';
import type IValidatable from '../validatable.interface';
import ValidationEngine from '../../../validation/validation-engine';
import type { IServerValidationError } from '../validatable.interface';
import { SanitizerService } from '../../../services/sanitizer.service';

export interface ICheckboxFormGroupState {
  value: string[];
  validationInfo: ValidationInfo;
}

interface ICheckboxFormGroupProps {
  id?: string;
  className?: string;
  serverValidationName?: string;
  labelText?: string;
  hideLabel?: boolean;
  ariaDescribedby?: string;
  helpTextElement?: JSX.Element;
  rowLayoutMdUp?: boolean;
  options: { value: string; text: string; iconElement?: JSX.Element; optionHelpTextElement?: JSX.Element; disabled?: boolean }[];
  required?: boolean;
  allowUnsafeHtml?: boolean;
  state: ICheckboxFormGroupState;
  onChange?: (value: string[]) => void;
  wideGap?: boolean;
}

export default forwardRef<IValidatable, ICheckboxFormGroupProps>(function CheckboxFormGroup(
  {
    id,
    className,
    serverValidationName = undefined,
    labelText,
    hideLabel = false,
    ariaDescribedby,
    helpTextElement,
    rowLayoutMdUp = false,
    options,
    required = false,
    allowUnsafeHtml = false,
    state,
    onChange,
    wideGap = false,
  }: ICheckboxFormGroupProps,
  ref,
) {
  const generatedId = useRef(IdHelper.newUniqueId('checkbox-group-form-group-'));
  const idWithFallback = id ?? generatedId.current;
  const helpTextId = helpTextElement ? `${idWithFallback}-help` : undefined;

  // === INTERFACE IMPLEMENTATION ===
  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      const updatedValidationInfo = new ValidationInfo(`${labelText} - `);
      ValidationEngine.for(updatedValidationInfo).if(required).notEmpty(state.value).endif().validate();
      return updatedValidationInfo;
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo(
        `${labelText} - `,
        errors
          .filter((e) => e.name === serverValidationName)
          .map((e) => e.errorMessages)
          .reduce((a, b) => a.concat(b), []),
      );
    },
  }));

  // === EVENT HANDLERS ===
  const handleCheckboxChange = (optionValue: string) => {
    if (state.value.includes(optionValue)) {
      onChange?.(state.value.filter((value) => value !== optionValue));
    } else {
      onChange?.([...state.value, optionValue]);
    }
  };

  // === HELPERS ===
  const getAriaDescribedBy = (optionValue: string, hasOptionHelpText: boolean) => {
    const describedBy = [state.validationInfo.isInvalid && `${idWithFallback}-validation-error`, ariaDescribedby, hasOptionHelpText && `${idWithFallback}-${optionValue.toLocaleLowerCase()}-help`]
      .filter(Boolean)
      .join(' ');

    return describedBy ? { 'aria-describedby': describedBy } : {};
  };

  return (
    <fieldset className={`checkbox-group-form-group ${className ?? ''}`}>
      {labelText && (
        <legend className={hideLabel ? 'visually-hidden' : ''}>
          {labelText}
          {required && <RequiredAsterisk />}
        </legend>
      )}
      <div className={`d-flex flex-wrap flex-column ${wideGap ? 'gap-3' : 'gap-2'} ${rowLayoutMdUp ? 'flex-sm-row' : ''}`}>
        {options.map(({ value, text, iconElement, optionHelpTextElement, disabled }) => (
          <div key={value} className={`form-check mb-0 me-2 ${state.validationInfo.isInvalid ? 'is-invalid' : ''} ${rowLayoutMdUp ? '' : ''}`}>
            <input
              type="checkbox"
              className={`form-check-input ${state.validationInfo.isInvalid ? 'is-invalid' : ''}`}
              name={idWithFallback}
              id={`${idWithFallback}-${value.toLocaleLowerCase()}`}
              value={value}
              checked={state.value.includes(value)}
              onChange={() => {
                handleCheckboxChange(value);
              }}
              aria-invalid={state.validationInfo.isInvalid}
              {...getAriaDescribedBy(value, !!optionHelpTextElement)}
              disabled={disabled}
            />
            <label className="form-check-label d-flex align-items-center" htmlFor={`${idWithFallback}-${value.toLocaleLowerCase()}`}>
              {iconElement}
              {allowUnsafeHtml && <span dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(text) }} />}
              {!allowUnsafeHtml && <span>{text}</span>}
            </label>
            {optionHelpTextElement && (
              <div id={`${idWithFallback}-${value.toLocaleLowerCase()}-help`} className="mt-1">
                {optionHelpTextElement}
              </div>
            )}
          </div>
        ))}
        {state.validationInfo.isInvalid && (
          <div id={`${idWithFallback}-validation-error`} className="invalid-feedback">
            {state.validationInfo.errors.map((error) => (
              <div key={error}>{error}</div>
            ))}
          </div>
        )}
        {helpTextElement && (
          <div className="form-text">
            <div id={helpTextId}>{helpTextElement}</div>
          </div>
        )}
      </div>
    </fieldset>
  );
});
